import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Item,
  PositionCatalog,
} from "@/services/backend/htz/position-catalog/types/position-catalog";
import { cn } from "@/shared/lib/utils";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * AddPositionDropdown offers the items of the position catalog, grouped by
 * category. The document the position is added to handles the chosen item.
 */
export function AddPositionDropdown({
  catalog,
  onAdd,
  disabled,
}: {
  catalog: PositionCatalog;
  onAdd: (item: Item) => void;
  disabled: boolean;
}) {
  const categories = uniqueCategories(catalog.items);
  // "positions" holds the error of a document without positions.
  const { invalid, reset } = useFieldErrors("positions");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          size="sm"
          aria-invalid={invalid || undefined}
          className={cn(invalid && "ring-destructive ring-2")}
        >
          <Plus className="mr-2 h-4 w-4" />
          <span>{t("Position")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {categories.map((category) => (
          <DropdownMenuGroup key={category}>
            <DropdownMenuLabel>{category}</DropdownMenuLabel>
            {catalog.items
              .filter((item) => item.category === category)
              .map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  onClick={() => {
                    reset();
                    onAdd(item);
                  }}
                >
                  {item.title}
                </DropdownMenuItem>
              ))}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function uniqueCategories(items: Item[]): string[] {
  return items.reduce((acc, cur) => {
    if (acc.indexOf(cur.category) !== -1) {
      return acc;
    }
    return [...acc, cur.category];
  }, [] as string[]);
}
